import { Injectable } from "@angular/core";
import { AuthenticationModel } from "../models/authentication.model";
import { AuthenticationResultModel } from "../models/authenticationResult.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { nameof } from '../utils/nameof.util';
import { firstValueFrom } from "rxjs";
import { TokenResponseModel } from "../models/responses/token.response.model";
import { TokenService } from "./token.service";
import { CatchHandler } from "../utils/catchHandler.util";
import { PoNotificationService } from "@po-ui/ng-components";
import { environment } from "../environments/environment";
import { GlobalService } from "./global.service";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    constructor(
        private http: HttpClient,
        private tokenService: TokenService,
        private poNotification: PoNotificationService,
        private catchHandler: CatchHandler,
        private globalService: GlobalService
    ) {
    }

    private authenticationEndpoint: string = 'api/totvsmoda/internal/authorization/v2/customer-user-token';
    private authorizationEndpoint: string = 'api/totvsmoda/internal/authorization/v2/customer-component-token';
    private authorizationRecoveryEndpoint: string = 'api/totvsmoda/internal/authorization/v2/customer-recovery-token';

    get apiHost(): string {
        return this.globalService.getClientHost() || environment.API_URL;
    }

    public async authentication(authenticationModel: AuthenticationModel): Promise<AuthenticationResultModel> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        const formData = new URLSearchParams();

        formData.set(nameof<AuthenticationModel>("cpfCnpj"), authenticationModel.cpfCnpj);
        formData.set(nameof<AuthenticationModel>("password"), authenticationModel.password);
        formData.set(nameof<AuthenticationModel>("tenant"), authenticationModel.tenant);
        formData.set(nameof<AuthenticationModel>("product"), authenticationModel.product.toString());

        const request = this.http.post<TokenResponseModel>(
            this.apiHost + this.authenticationEndpoint,
            formData.toString(),
            {
                headers: headers
            }
        );

        return await firstValueFrom(request)
            .then((res) => {
                this.tokenService.token = res.access_token;
                return {
                    isAuthenticated: true
                };
            })
            .catch((err) => {
                const errors = this.catchHandler.handle(err);

                if (errors.length === 0)
                    return {
                        isAuthenticated: false
                    };

                errors.forEach(error => {
                    this.poNotification.warning(error.message);
                })

                return {
                    isAuthenticated: false
                }
            });
    }

    public async authorization(branchCode: number): Promise<boolean> {
        const request = this.http.post<TokenResponseModel>(
            this.apiHost + this.authorizationEndpoint,
            {
                branchCode: branchCode
            }
        );

        return await firstValueFrom(request)
            .then((res) => {
                this.tokenService.token = res.access_token;
                return true;
            })
            .catch((err) => {
                const errors = this.catchHandler.handle(err);

                if (errors.length === 0) {
                    return false;
                }

                errors.forEach(error => {
                    this.poNotification.warning(error.message);
                })

                return false;
            });
    }

    public async authenticationRecovery(authenticationModel: AuthenticationModel): Promise<AuthenticationResultModel> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        const formData = new URLSearchParams();

        formData.set(nameof<AuthenticationModel>("tenant"), authenticationModel.tenant);
        formData.set(nameof<AuthenticationModel>("cpfCnpj"), authenticationModel.cpfCnpj);
        formData.set(nameof<AuthenticationModel>("email"), authenticationModel.email);
        formData.set(nameof<AuthenticationModel>("product"), authenticationModel.product.toString());

        const request = this.http.post<TokenResponseModel>(
            this.apiHost + this.authorizationRecoveryEndpoint,
            formData.toString(),
            {
                headers: headers
            }
        );

        return await firstValueFrom(request)
            .then((res) => {
                this.tokenService.token = res.access_token;
                return {
                    isAuthenticated: true
                };
            })
            .catch((err) => {
                const errors = this.catchHandler.handle(err);

                if (errors.length === 0)
                    return {
                        isAuthenticated: false
                    };

                errors.forEach(error => {
                    this.poNotification.warning(error.message);
                })

                return {
                    isAuthenticated: false
                }
            });
    }
}
